// useRelecturaPeriodica.ts — vuelve a leer lo que muestra el panel mientras la persona lo está MIRANDO (round 2026-10-30, B3).
//
// Por qué existe: el modo real (simulación o REAL) solo se releía tras un cambio propio, al apretar «Actualizar» o al volver a
// la ventana. Si otro administrador lo encendía mientras esta pantalla seguía abierta, decía «Simulación (nada se guarda)»
// con el servidor ya en REAL: justo la confusión entre pruebas y lo real que el dueño quiere evitar (y quien solo mira, sin
// poder cambiarlo, es quien más se confía).
//
// Lee cada `intervaloMs` (45 s) y SOLO si se cumplen las dos cosas:
//   · la pestaña del navegador está visible (`document.visibilityState`): en segundo plano no se gasta red ni batería;
//   · `activa`: la pestaña «Asistente de WhatsApp» de /configuracion es la que se ve. AntD deja montadas las pestañas ya
//     visitadas aunque estén ocultas, así que sin esta bandera seguiría leyendo con otra pestaña de Configuración abierta.
// Además lee UNA vez, de inmediato, al volver a la ventana y al volver a esta pestaña (no al montar: la carga inicial ya la
// hace cada dato por su cuenta).
//
// TOPE DE LECTURAS EN VUELO (B3c). Este hook solo avisa «toca leer»; lo que se lee lo decide quien lo usa. `apiFetch` no tiene tiempo
// máximo, así que con el servidor colgado cada latido lanzaba otra tanda de lecturas sin que las anteriores hubieran vuelto (18 GET
// tras 6 latidos, medidos): con HTTP/1.1 (6 conexiones por host) podían ocupar las conexiones y retrasar lo que la persona hace.
// `crearLectorSinSolapes` es la guarda: no lanza una lectura de un tipo mientras la anterior DEL MISMO TIPO siga en vuelo (así un
// ajuste colgado no deja sin releer el modo, que es lo importante).
import { useEffect, useRef } from "react";

export const INTERVALO_RELECTURA_MS = 45_000;

/**
 * Cuántas veces seguidas se omite una lectura de un tipo cuya anterior sigue en vuelo, antes de dejar pasar otra de todos modos.
 * Sin esta válvula, una lectura que NUNCA vuelve (sin tiempo máximo en `fetch`) dejaría ese dato sin releerse nunca más y la
 * pantalla ni siquiera avisaría que está desactualizada. Con 3, la peor espera son 4 latidos (3 minutos) entre una y otra.
 */
export const MAX_OMISIONES_EN_VUELO = 3;

/**
 * Fábrica de la guarda de lecturas en vuelo. Devuelve `leer(clave, lectura)`: si ya hay una lectura de esa `clave` sin terminar,
 * no lanza otra (devuelve false y la cuenta como omitida); si no, la lanza (true) y la marca en vuelo hasta que termine, bien o mal.
 * Una lectura que termina después de haber sido reemplazada por la válvula no suelta la marca de la nueva.
 */
export function crearLectorSinSolapes(maxOmisiones: number = MAX_OMISIONES_EN_VUELO) {
  const enVuelo = new Map<string, { omitidas: number }>();
  return function leer(clave: string, lectura: () => Promise<unknown> | void): boolean {
    const previa = enVuelo.get(clave);
    if (previa && previa.omitidas < maxOmisiones) {
      previa.omitidas += 1;
      return false;
    }
    const propia = { omitidas: 0 };
    enVuelo.set(clave, propia);
    const soltar = () => {
      if (enVuelo.get(clave) === propia) enVuelo.delete(clave);
    };
    try {
      Promise.resolve(lectura()).then(soltar, soltar);
    } catch {
      soltar();
    }
    return true;
  };
}

export function useRelecturaPeriodica(alLeer: () => void, { activa, intervaloMs = INTERVALO_RELECTURA_MS }: { activa: boolean; intervaloMs?: number }): void {
  const alLeerRef = useRef(alLeer);
  alLeerRef.current = alLeer;
  /** Para no leer dos veces al montar (la carga inicial ya lo hizo): solo se lee al VOLVER. */
  const yaEstuvoActiva = useRef(false);

  useEffect(() => {
    if (!activa) return;
    const visible = () => document.visibilityState !== "hidden";
    let id: ReturnType<typeof setInterval> | null = null;
    const parar = () => {
      if (id !== null) {
        clearInterval(id);
        id = null;
      }
    };
    const arrancar = () => {
      if (id === null) id = setInterval(() => alLeerRef.current(), intervaloMs);
    };

    // Volver a esta pestaña tras haber estado en otra: se lee ya, no dentro de 45 s.
    if (yaEstuvoActiva.current && visible()) alLeerRef.current();
    yaEstuvoActiva.current = true;

    if (visible()) arrancar();
    const alCambiarVisibilidad = () => {
      if (visible()) {
        alLeerRef.current();
        arrancar();
      } else {
        parar();
      }
    };
    document.addEventListener("visibilitychange", alCambiarVisibilidad);
    return () => {
      parar();
      document.removeEventListener("visibilitychange", alCambiarVisibilidad);
    };
  }, [activa, intervaloMs]);
}
